/**
 * Движок пиктограмм упражнений: поза → суставы → кадры анимации.
 *
 * ЗАЧЕМ ОН. Миниатюра упражнения отвечает на один вопрос — «что это за
 * движение» — раньше, чем человек прочитает название. Рисунки из свободных
 * источников отвечали на него плохо: в плитке 56 px подробная штриховка
 * сливалась в пятно, а у семи движений рисунка не было вовсе. Пиктограмма
 * нарисована под этот размер и ДВИЖЕТСЯ: жим от тяги отличает траектория, а
 * не поза.
 *
 * КАК УСТРОЕНО. Поза задаётся не координатами всех суставов, а тем, что про
 * движение известно: где таз, куда смотрит корпус, где кисти и стопы (на
 * грифе, на полу, на скамье). Локти и колени ставит обратная кинематика —
 * двухзвенная, с постоянными длинами костей. Поэтому рука не удлиняется в
 * середине тяги, как было бы при простой интерполяции координат суставов:
 * интерполируются ОПОРНЫЕ ТОЧКИ, а сустав на каждом кадре решается заново.
 *
 * Там, где проекция честно укорачивает кость (локоть в жиме лёжа уходит на
 * зрителя, бедро сидящего спереди смотрит в экран), сустав ставится рукой —
 * `elbowAt`, `kneeAt`: постоянная длина там была бы неправдой.
 *
 * Анимацию ведёт сам браузер (SMIL `<animate>`): движок один раз запекает
 * кадры в строки значений, и дальше ни одного вызова JS на кадр. На экране
 * «Сегодня» таких фигурок семь, и крутить ради них requestAnimationFrame —
 * греть телефон в кармане.
 *
 * ОРИЕНТАЦИЯ. Все фигуры вида сбоку смотрят ВПРАВО или получены поворотом
 * такой фигуры (лёжа на спине — голова слева, лёжа на животе — голова
 * справа). Зеркальных нет: у отражённой фигуры колени и локти гнулись бы в
 * обратную сторону, и знаки сгиба пришлось бы помнить для каждой позы.
 *
 * Здесь нет ни React, ни DOM: модуль чистый, его результат рисует
 * `components/ExerciseFigure.tsx`.
 */

export type Pt = readonly [number, number]

/**
 * Куда привязан снаряд: точка холста, сустав по имени или место на кости —
 * доля пути от сустава к суставу со сдвигом вбок (валик тренажёра лежит на
 * голени и поворачивается вместе с ней).
 */
export type Ref = Pt | string | { from: string; to: string; t: number; off?: number }

// --------------------------------------------------------------------------- //
// Пропорции фигуры (холст 100×100)
// --------------------------------------------------------------------------- //

const TORSO = 25
const NECK = 10.5
export const HEAD_R = 6.4
const UPPER_ARM = 13
const FOREARM = 13
const THIGH = 20
const SHIN = 20.5
const FOOT = 8.5

/** Вид спереди: полуширина плеч и таза. */
const SHOULDER_HALF = 9.5
const HIP_HALF = 4.6

/** Дальняя рука и нога чуть смещены: так читается глубина. */
const FAR_HAND: Pt = [2.4, -1]
const FAR_FOOT: Pt = [3.2, -0.4]

// --------------------------------------------------------------------------- //
// Позы
// --------------------------------------------------------------------------- //

/**
 * Поза вида сбоку. Углы — в градусах по часовой стрелке от направления
 * «вверх»: 0 — вверх, 90 — вправо (вперёд), 180 — вниз, −90 — влево.
 */
export interface SidePose {
  hip: Pt
  /** Направление от таза к плечам. */
  torso: number
  /** Направление от плеч к голове. По умолчанию — как у корпуса. */
  neck?: number
  hand: Pt
  handF?: Pt
  /** Знак сгиба локтя. +1 — анатомический для фигуры лицом вправо. */
  elbow?: number
  elbowF?: number
  /** Локоть, поставленный рукой (проекция укорачивает плечо). */
  elbowAt?: Pt
  elbowFAt?: Pt
  foot: Pt
  footF?: Pt
  /** Знак сгиба колена. −1 — анатомический для фигуры лицом вправо. */
  knee?: number
  kneeF?: number
  kneeAt?: Pt
  kneeFAt?: Pt
  /** Направление стопы (от лодыжки к носку). По умолчанию 93 — плашмя вперёд. */
  toe?: number
  toeF?: number
}

/**
 * Поза вида спереди. Задаётся правая (для зрителя) половина; левая —
 * отражение относительно оси тела.
 */
export interface FrontPose {
  hip: Pt
  hand: Pt
  /** Знак сгиба локтя правой руки; у левой он зеркальный. */
  elbow?: number
  elbowAt?: Pt
  foot: Pt
  knee?: number
  kneeAt?: Pt
  /**
   * Перспективное сокращение рук (1 — в полную длину). В виде спереди рука,
   * вытянутая на зрителя, короче — иначе сведение рук не нарисовать.
   */
  arm?: number
}

// --------------------------------------------------------------------------- //
// Снаряды и обстановка
// --------------------------------------------------------------------------- //

/** `gear` — снаряд в акцентном цвете, `prop` — обстановка (скамья, стойка). */
export type Tone = 'gear' | 'prop'

export type Prim =
  /** Отрезок: трос, рычаг тренажёра, рукоять. `da`/`db` — сдвиг концов. */
  | { k: 'line'; a: Ref; b: Ref; w: number; tone: Tone; back?: boolean; da?: Pt; db?: Pt }
  /** Блин с торца: кольцо и втулка. Так выглядит штанга в виде сбоку. */
  | { k: 'plate'; at: Ref; r?: number; d?: Pt; back?: boolean }
  /** Закрашенный круг: ролик блока, валик тренажёра, перекладина с торца. */
  | { k: 'disc'; at: Ref; r: number; tone: Tone; d?: Pt; back?: boolean }
  /**
   * Гантель сбоку: рукоять и два блина. Рукоять идёт вдоль угла `ang` либо
   * поперёк кости `across` (в разгибаниях гантель поворачивается с предплечьем).
   */
  | { k: 'db'; at: Ref; ang?: number; across?: [string, string]; d?: Pt; back?: boolean }
  /** Гриф в виде спереди: через обе кисти, с выносом `over` и блинами. */
  | { k: 'bar'; a: Ref; b: Ref; over: number; plates?: boolean; back?: boolean }
  /** Неподвижный скруглённый прямоугольник: скамья, сиденье, стек. */
  | { k: 'rect'; x: number; y: number; w: number; h: number; r?: number; rot?: number; tone?: Tone }

export interface FigureSpec {
  /** Название — для галереи и сверки со справочником, на экране не показывается. */
  label: string
  view: 'side' | 'front'
  /** Начало повторения. */
  a: SidePose | FrontPose
  /** Другой конец движения. Нет — удержание (планка). */
  b?: SidePose | FrontPose
  prims?: Prim[]
  /** Линия пола: y-координата. */
  floor?: number
  /** Кадрирование: [x, y, сторона квадрата]. По умолчанию весь холст. */
  box?: readonly [number, number, number]
  /** Доля пути между позами для неподвижного показа (без анимации). */
  still?: number
}

// --------------------------------------------------------------------------- //
// Геометрия
// --------------------------------------------------------------------------- //

const RAD = Math.PI / 180

/** Единичный вектор по углу: 0 — вверх, по часовой стрелке. */
export function dir(deg: number): Pt {
  return [Math.sin(deg * RAD), -Math.cos(deg * RAD)]
}

export function add(p: Pt, v: Pt, k = 1): Pt {
  return [p[0] + v[0] * k, p[1] + v[1] * k]
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function lerpPt(a: Pt, b: Pt, t: number): Pt {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t)]
}

/**
 * Двухзвенная обратная кинематика: где локоть, если плечо и кисть известны.
 *
 * Цель вне досягаемости — конечность вытягивается в её сторону на полную
 * длину, а не рвётся: кисть встаёт туда, куда дотянулась, и снаряд едет за
 * ней (он привязан к суставу, а не к запрошенной точке).
 */
function solve(root: Pt, target: Pt, l1: number, l2: number, bend: number): { mid: Pt; end: Pt } {
  const dx = target[0] - root[0]
  const dy = target[1] - root[1]
  const dist = Math.hypot(dx, dy)
  const reach = Math.min(l1 + l2 - 0.01, Math.max(Math.abs(l1 - l2) + 0.01, dist))
  const ux = dist > 0 ? dx / dist : 0
  const uy = dist > 0 ? dy / dist : 1
  const end: Pt = [root[0] + ux * reach, root[1] + uy * reach]
  const cos = (l1 * l1 + reach * reach - l2 * l2) / (2 * l1 * reach)
  const inner = Math.acos(Math.min(1, Math.max(-1, cos)))
  const angle = Math.atan2(uy, ux) + bend * inner
  return { mid: [root[0] + l1 * Math.cos(angle), root[1] + l1 * Math.sin(angle)], end }
}

export type Joints = Record<string, Pt>

interface FullSide {
  hip: Pt
  torso: number
  neck: number
  hand: Pt
  handF: Pt
  elbow: number
  elbowF: number
  foot: Pt
  footF: Pt
  knee: number
  kneeF: number
  toe: number
  toeF: number
}

function fillSide(p: SidePose): FullSide {
  const elbow = p.elbow ?? 1
  const knee = p.knee ?? -1
  const toe = p.toe ?? 93
  return {
    hip: p.hip,
    torso: p.torso,
    neck: p.neck ?? p.torso,
    hand: p.hand,
    handF: p.handF ?? add(p.hand, FAR_HAND),
    elbow,
    elbowF: p.elbowF ?? elbow,
    foot: p.foot,
    footF: p.footF ?? add(p.foot, FAR_FOOT),
    knee,
    kneeF: p.kneeF ?? knee,
    toe,
    toeF: p.toeF ?? toe,
  }
}

function mixSide(a: FullSide, b: FullSide, t: number): FullSide {
  return {
    hip: lerpPt(a.hip, b.hip, t),
    torso: lerp(a.torso, b.torso, t),
    neck: lerp(a.neck, b.neck, t),
    hand: lerpPt(a.hand, b.hand, t),
    handF: lerpPt(a.handF, b.handF, t),
    elbow: lerp(a.elbow, b.elbow, t),
    elbowF: lerp(a.elbowF, b.elbowF, t),
    foot: lerpPt(a.foot, b.foot, t),
    footF: lerpPt(a.footF, b.footF, t),
    knee: lerp(a.knee, b.knee, t),
    kneeF: lerp(a.kneeF, b.kneeF, t),
    toe: lerp(a.toe, b.toe, t),
    toeF: lerp(a.toeF, b.toeF, t),
  }
}

function sideJoints(p: FullSide): Joints {
  const shoulder = add(p.hip, dir(p.torso), TORSO)
  const head = add(shoulder, dir(p.neck), NECK)
  const arm = solve(shoulder, p.hand, UPPER_ARM, FOREARM, p.elbow)
  const armF = solve(shoulder, p.handF, UPPER_ARM, FOREARM, p.elbowF)
  const leg = solve(p.hip, p.foot, THIGH, SHIN, p.knee)
  const legF = solve(p.hip, p.footF, THIGH, SHIN, p.kneeF)
  return {
    hip: p.hip,
    shoulder,
    head,
    elbow: arm.mid,
    hand: arm.end,
    elbowF: armF.mid,
    handF: armF.end,
    knee: leg.mid,
    foot: leg.end,
    toe: add(leg.end, dir(p.toe), FOOT),
    kneeF: legF.mid,
    footF: legF.end,
    toeF: add(legF.end, dir(p.toeF), FOOT),
  }
}

function sideAt(spec: FigureSpec, t: number): Joints {
  const poseA = spec.a as SidePose
  const poseB = (spec.b ?? spec.a) as SidePose
  const a = fillSide(poseA)
  const b = fillSide(poseB)
  const mixed = mixSide(a, b, t)
  const joints = sideJoints(mixed)

  // Суставы, поставленные рукой. Если задан только в одной из поз, во второй
  // берётся решение кинематики — переход между ними остаётся плавным.
  const endA = sideJoints(a)
  const endB = sideJoints(b)
  const pin = (joint: string, pa: Pt | undefined, pb: Pt | undefined): boolean => {
    if (!pa && !pb) return false
    const from = pa ?? endA[joint]
    const to = pb ?? endB[joint]
    if (from && to) joints[joint] = lerpPt(from, to, t)
    return true
  }

  if (pin('elbow', poseA.elbowAt, poseB.elbowAt)) joints.hand = mixed.hand
  const farElbowA = poseA.elbowFAt ?? (poseA.elbowAt ? add(poseA.elbowAt, FAR_HAND) : undefined)
  const farElbowB = poseB.elbowFAt ?? (poseB.elbowAt ? add(poseB.elbowAt, FAR_HAND) : undefined)
  if (pin('elbowF', farElbowA, farElbowB)) joints.handF = mixed.handF

  if (pin('knee', poseA.kneeAt, poseB.kneeAt)) {
    joints.foot = mixed.foot
    joints.toe = add(mixed.foot, dir(mixed.toe), FOOT)
  }
  const farKneeA = poseA.kneeFAt ?? (poseA.kneeAt ? add(poseA.kneeAt, FAR_FOOT) : undefined)
  const farKneeB = poseB.kneeFAt ?? (poseB.kneeAt ? add(poseB.kneeAt, FAR_FOOT) : undefined)
  if (pin('kneeF', farKneeA, farKneeB)) {
    joints.footF = mixed.footF
    joints.toeF = add(mixed.footF, dir(mixed.toeF), FOOT)
  }
  return joints
}

interface FullFront {
  hip: Pt
  hand: Pt
  elbow: number
  foot: Pt
  knee: number
  arm: number
}

function fillFront(p: FrontPose): FullFront {
  return {
    hip: p.hip,
    hand: p.hand,
    elbow: p.elbow ?? 1,
    foot: p.foot,
    knee: p.knee ?? 1,
    arm: p.arm ?? 1,
  }
}

function mixFront(a: FullFront, b: FullFront, t: number): FullFront {
  return {
    hip: lerpPt(a.hip, b.hip, t),
    hand: lerpPt(a.hand, b.hand, t),
    elbow: lerp(a.elbow, b.elbow, t),
    foot: lerpPt(a.foot, b.foot, t),
    knee: lerp(a.knee, b.knee, t),
    arm: lerp(a.arm, b.arm, t),
  }
}

function mirror(p: Pt, axis: number): Pt {
  return [2 * axis - p[0], p[1]]
}

function frontJoints(p: FullFront): Joints {
  const axis = p.hip[0]
  const shoulder: Pt = [axis, p.hip[1] - TORSO]
  const head: Pt = [axis, shoulder[1] - NECK]
  const shR: Pt = [axis + SHOULDER_HALF, shoulder[1] + 1.5]
  const hipR: Pt = [axis + HIP_HALF, p.hip[1]]
  const arm = solve(shR, p.hand, UPPER_ARM * p.arm, FOREARM * p.arm, p.elbow)
  const leg = solve(hipR, p.foot, THIGH, SHIN, p.knee)
  return {
    hip: p.hip,
    shoulder,
    head,
    shR,
    hipR,
    elR: arm.mid,
    haR: arm.end,
    knR: leg.mid,
    ftR: leg.end,
  }
}

function frontAt(spec: FigureSpec, t: number): Joints {
  const poseA = spec.a as FrontPose
  const poseB = (spec.b ?? spec.a) as FrontPose
  const a = fillFront(poseA)
  const b = fillFront(poseB)
  const mixed = mixFront(a, b, t)
  const joints = frontJoints(mixed)
  const endA = frontJoints(a)
  const endB = frontJoints(b)

  if (poseA.elbowAt || poseB.elbowAt) {
    const from = poseA.elbowAt ?? endA.elR
    const to = poseB.elbowAt ?? endB.elR
    if (from && to) joints.elR = lerpPt(from, to, t)
    joints.haR = mixed.hand
  }
  if (poseA.kneeAt || poseB.kneeAt) {
    const from = poseA.kneeAt ?? endA.knR
    const to = poseB.kneeAt ?? endB.knR
    if (from && to) joints.knR = lerpPt(from, to, t)
    joints.ftR = mixed.foot
  }

  // Левая половина — отражение правой. «grip» — середина между кистями: к
  // ней крепится трос верхнего блока.
  const axis = mixed.hip[0]
  for (const [right, left] of [
    ['shR', 'shL'],
    ['hipR', 'hipL'],
    ['elR', 'elL'],
    ['haR', 'haL'],
    ['knR', 'knL'],
    ['ftR', 'ftL'],
  ] as const) {
    const source = joints[right]
    if (source) joints[left] = mirror(source, axis)
  }
  const hand = joints.haR
  if (hand) joints.grip = [axis, hand[1]]
  return joints
}

/** Суставы фигуры на доле пути `t` между позами (0 — начало, 1 — другой конец). */
export function jointsAt(spec: FigureSpec, t: number): Joints {
  return spec.view === 'side' ? sideAt(spec, t) : frontAt(spec, t)
}

// --------------------------------------------------------------------------- //
// Модель для отрисовки
// --------------------------------------------------------------------------- //

export type LayerClass =
  | 'fg-far'
  | 'fg-near'
  | 'fg-body'
  | 'fg-torso'
  | 'fg-gear'
  | 'fg-gear-fill'
  | 'fg-prop'
  | 'fg-prop-fill'

/**
 * Элемент рисунка. У подвижного атрибута значений столько же, сколько кадров;
 * неподвижный лежит в `attrs`, и `<animate>` для него не создаётся.
 */
export interface Shape {
  tag: 'path' | 'circle' | 'rect'
  cls: LayerClass
  attrs: Record<string, string | number>
  frames: Record<string, string[]>
}

export interface FigureModel {
  viewBox: string
  shapes: Shape[]
  /** Моменты кадров, 0..1 — для `keyTimes`. Пусто — фигура неподвижна. */
  keyTimes: number[]
  /** Длительность цикла, секунды. */
  duration: number
}

const n1 = (v: number): string => (Math.round(v * 10) / 10).toString()
const pt = (p: Pt): string => `${n1(p[0])} ${n1(p[1])}`
const poly = (points: Pt[]): string => 'M' + points.map(pt).join('L')

function joint(name: string, joints: Joints): Pt {
  const found = joints[name]
  if (!found) throw new Error(`Нет сустава «${name}»`)
  return found
}

function resolve(ref: Ref, joints: Joints, shift?: Pt): Pt {
  let base: Pt
  if (typeof ref === 'string') {
    base = joint(ref, joints)
  } else if (Array.isArray(ref)) {
    base = ref as Pt
  } else {
    const bone = ref as { from: string; to: string; t: number; off?: number }
    const from = joint(bone.from, joints)
    const to = joint(bone.to, joints)
    const len = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1
    const normal: Pt = [-(to[1] - from[1]) / len, (to[0] - from[0]) / len]
    base = add(lerpPt(from, to, bone.t), normal, bone.off ?? 0)
  }
  return shift ? add(base, shift) : base
}

/** Кости вида сбоку: дальние конечности первыми — они лежат под корпусом. */
type Bone = [LayerClass, number, string[]]

const SIDE_BONES: Bone[] = [
  ['fg-far', 6.4, ['shoulder', 'elbowF', 'handF']],
  ['fg-far', 8, ['hip', 'kneeF', 'footF', 'toeF']],
  ['fg-near', 12.5, ['shoulder', 'hip']],
  ['fg-near', 8, ['hip', 'knee', 'foot', 'toe']],
]
const SIDE_ARM: Bone = ['fg-near', 6.4, ['shoulder', 'elbow', 'hand']]

const FRONT_LEGS: Bone[] = [
  ['fg-near', 8, ['hipR', 'knR', 'ftR']],
  ['fg-near', 8, ['hipL', 'knL', 'ftL']],
]
const FRONT_ARMS: Bone[] = [
  ['fg-near', 6.4, ['shR', 'elR', 'haR']],
  ['fg-near', 6.4, ['shL', 'elL', 'haL']],
]

function frontTorso(j: Joints): string {
  const shR = joint('shR', j)
  const shL = joint('shL', j)
  const hipR = joint('hipR', j)
  const hipL = joint('hipL', j)
  return `M${pt(add(shL, [1, 0]))}L${pt(add(shR, [-1, 0]))}L${pt(add(hipR, [1, 0]))}L${pt(add(hipL, [-1, 0]))}Z`
}

/**
 * Ход цикла: пауза в начале, движение, короткая пауза в другом конце, возврат
 * и снова пауза. Паузы — не украшение: без них глаз не успевает увидеть
 * крайние положения, а узнают движение именно по ним.
 */
const STEPS = 7
const HOLD_START = 0.1
const MOVE = 0.36
const HOLD_END = 0.1

const ease = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

function timeline(): { times: number[]; phases: number[] } {
  const times: number[] = [0]
  const phases: number[] = [0]
  for (let i = 0; i <= STEPS; i++) {
    times.push(HOLD_START + (MOVE * i) / STEPS)
    phases.push(ease(i / STEPS))
  }
  const backStart = HOLD_START + MOVE + HOLD_END
  for (let i = 0; i <= STEPS; i++) {
    times.push(Math.min(1, backStart + (MOVE * i) / STEPS))
    phases.push(ease(1 - i / STEPS))
  }
  times.push(1)
  phases.push(0)
  return { times, phases }
}

function isBackdrop(p: Prim): boolean {
  if (p.k === 'rect') return true
  if (p.k === 'line' && p.tone === 'prop' && p.back !== false) return true
  return 'back' in p && p.back === true
}

/** Собрать рисунок упражнения: элементы, их кадры и расписание цикла. */
export function buildFigure(spec: FigureSpec, animated: boolean): FigureModel {
  const moving = animated && spec.b !== undefined
  const still = spec.b ? (spec.still ?? 0.5) : 0
  const { times, phases } = moving ? timeline() : { times: [], phases: [still] }
  const poses = phases.map((t) => jointsAt(spec, t))

  const shapes: Shape[] = []
  const push = (
    tag: Shape['tag'],
    cls: LayerClass,
    attrs: Shape['attrs'],
    perFrame: Record<string, (j: Joints) => string>,
  ): void => {
    const frames: Record<string, string[]> = {}
    const fixed: Shape['attrs'] = { ...attrs }
    for (const [name, make] of Object.entries(perFrame)) {
      const values = poses.map(make)
      const first = values[0] ?? ''
      // Атрибут не меняется — он постоянный, и анимировать его незачем.
      if (values.every((v) => v === first)) fixed[name] = first
      else frames[name] = values
    }
    shapes.push({ tag, cls, attrs: fixed, frames })
  }

  const bones = (list: Bone[]): void => {
    for (const [cls, width, names] of list) {
      push('path', cls, { 'stroke-width': width }, {
        d: (j) => poly(names.map((name) => joint(name, j))),
      })
    }
  }

  const circle = (cls: LayerClass, attrs: Shape['attrs'], at: (j: Joints) => Pt): void => {
    push('circle', cls, attrs, {
      cx: (j) => n1(at(j)[0]),
      cy: (j) => n1(at(j)[1]),
    })
  }

  const prim = (p: Prim): void => {
    switch (p.k) {
      case 'rect':
        shapes.push({
          tag: 'rect',
          cls: p.tone === 'gear' ? 'fg-gear-fill' : 'fg-prop-fill',
          attrs: {
            x: p.x,
            y: p.y,
            width: p.w,
            height: p.h,
            rx: p.r ?? Math.min(p.w, p.h) / 2,
            ...(p.rot ? { transform: `rotate(${p.rot} ${n1(p.x + p.w / 2)} ${n1(p.y + p.h / 2)})` } : {}),
          },
          frames: {},
        })
        return
      case 'line':
        push('path', p.tone === 'gear' ? 'fg-gear' : 'fg-prop', { 'stroke-width': p.w }, {
          d: (j) => poly([resolve(p.a, j, p.da), resolve(p.b, j, p.db)]),
        })
        return
      case 'disc':
        circle(p.tone === 'gear' ? 'fg-gear-fill' : 'fg-prop-fill', { r: p.r }, (j) =>
          resolve(p.at, j, p.d),
        )
        return
      case 'plate': {
        const at = (j: Joints): Pt => resolve(p.at, j, p.d)
        const r = p.r ?? 10
        circle('fg-gear', { r, 'stroke-width': r > 7 ? 3.2 : 2.8 }, at)
        circle('fg-gear-fill', { r: r > 7 ? 2.4 : 1.8 }, at)
        return
      }
      case 'db': {
        const axisAt = (j: Joints): Pt => {
          if (!p.across) return dir(p.ang ?? 90)
          const from = joint(p.across[0], j)
          const to = joint(p.across[1], j)
          const len = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1
          return [-(to[1] - from[1]) / len, (to[0] - from[0]) / len]
        }
        const at = (j: Joints): Pt => resolve(p.at, j, p.d)
        push('path', 'fg-gear', { 'stroke-width': 2.6 }, {
          d: (j) => poly([add(at(j), axisAt(j), -5.5), add(at(j), axisAt(j), 5.5)]),
        })
        for (const side of [-1, 1]) {
          push('path', 'fg-gear', { 'stroke-width': 6.5 }, {
            d: (j) => poly([add(at(j), axisAt(j), side * 4.4), add(at(j), axisAt(j), side * 6)]),
          })
        }
        return
      }
      case 'bar': {
        const ends = (j: Joints): [Pt, Pt] => {
          const a = resolve(p.a, j)
          const b = resolve(p.b, j)
          const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
          const u: Pt = [(b[0] - a[0]) / len, (b[1] - a[1]) / len]
          return [add(a, u, -p.over), add(b, u, p.over)]
        }
        push('path', 'fg-gear', { 'stroke-width': 2.8 }, { d: (j) => poly(ends(j)) })
        if (p.plates) {
          for (const index of [0, 1] as const) {
            push('path', 'fg-gear', { 'stroke-width': 3.6 }, {
              d: (j) => {
                const pair = ends(j)
                const end = pair[index]
                const other = pair[index === 0 ? 1 : 0]
                const len = Math.hypot(end[0] - other[0], end[1] - other[1]) || 1
                const inward: Pt = [(other[0] - end[0]) / len, (other[1] - end[1]) / len]
                // Блин — короткая толстая черта поперёк грифа у его конца.
                const normal: Pt = [-inward[1], inward[0]]
                const center = add(end, inward, 3.5)
                return poly([add(center, normal, -8), add(center, normal, 8)])
              },
            })
          }
        }
        return
      }
    }
  }

  const prims = spec.prims ?? []
  if (spec.floor !== undefined) {
    shapes.push({
      tag: 'path',
      cls: 'fg-prop',
      attrs: { 'stroke-width': 2, d: `M4 ${spec.floor}H96` },
      frames: {},
    })
  }
  // Обстановка и снаряд «за спиной» — под фигурой, остальной снаряд — поверх.
  for (const p of prims) if (isBackdrop(p)) prim(p)

  const head = (): void => circle('fg-body', { r: HEAD_R }, (j) => joint('head', j))

  if (spec.view === 'side') {
    bones(SIDE_BONES)
    head()
    bones([SIDE_ARM])
  } else {
    bones(FRONT_LEGS)
    push('path', 'fg-torso', {}, { d: frontTorso })
    head()
    bones(FRONT_ARMS)
  }

  for (const p of prims) if (!isBackdrop(p)) prim(p)

  const box = spec.box ?? [0, 0, 100]
  return {
    viewBox: `${box[0]} ${box[1]} ${box[2]} ${box[2]}`,
    shapes,
    keyTimes: moving ? times : [],
    duration: 3,
  }
}
